import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { newsItems, tagColors } from "@/mocks/news";
import PageSEO from "@/components/base/PageSEO";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const news = newsItems.find(n => n.id === Number(id));

  if (!news) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-6 py-24">
          <i className="ri-newspaper-line text-[3rem] text-[#c9a98a] mb-4 block"></i>
          <h2 className="text-[#3d2b1f] text-[1.75rem] font-bold mb-3">找不到此消息</h2>
          <p className="text-[#7a6455] text-[1.0625rem] mb-8">消息可能已移除或連結有誤，請回最新消息列表。</p>
          <Link to="/news" className="px-8 py-3.5 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-semibold hover:bg-[#5a3f30] transition-colors whitespace-nowrap cursor-pointer">
            回最新消息
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const currentIndex = newsItems.findIndex(n => n.id === Number(id));
  const prevNews = currentIndex > 0 ? newsItems[currentIndex - 1] : null;
  const nextNews = currentIndex < newsItems.length - 1 ? newsItems[currentIndex + 1] : null;

  const otherNews = newsItems.filter(n => n.id !== news.id).slice(0, 3);

  const newsSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": news.title,
    "description": news.excerpt,
    "image": news.image,
    "datePublished": news.date,
    "author": { "@type": "Organization", "name": "台中設計師女裝Outlet" },
    "publisher": { "@type": "Organization", "name": "台中設計師女裝Outlet" },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.example.com/news/${news.id}` }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <PageSEO
        title={`${news.title}｜台中設計師女裝Outlet 最新消息`}
        description={news.excerpt}
        keywords={`${news.category},${news.tag},台中女裝活動,設計師品牌Outlet`}
        canonical={`https://www.example.com/news/${news.id}`}
        ogImage={news.image}
        schema={newsSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/70" />
          <div className="absolute inset-0 flex flex-col justify-end pb-12 px-6 md:px-16 max-w-[1100px] mx-auto left-0 right-0">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1.5 rounded text-[0.9375rem] font-semibold ${tagColors[news.tag] || "bg-[#c9a98a] text-[#3d2b1f]"}`}>
                {news.tag}
              </span>
              <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-[0.875rem] font-medium">{news.category}</span>
            </div>
            <h1 className="text-white text-[2rem] md:text-[2.625rem] font-bold leading-[1.35] mb-4">{news.title}</h1>
            <div className="flex items-center gap-2 text-white/75 text-[1rem]">
              <i className="ri-calendar-line"></i>
              <span>{news.date}</span>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-white border-b border-[#ede8e0]">
          <div className="w-full px-6 md:px-16 max-w-[1100px] mx-auto py-3">
            <nav className="flex items-center gap-2 text-[0.9375rem] text-[#a09080]">
              <Link to="/" className="hover:text-[#3d2b1f] transition-colors cursor-pointer">首頁</Link>
              <span>/</span>
              <Link to="/news" className="hover:text-[#3d2b1f] transition-colors cursor-pointer">最新消息</Link>
              <span>/</span>
              <span className="text-[#3d2b1f] line-clamp-1">{news.title}</span>
            </nav>
          </div>
        </div>

        {/* Content */}
        <article className="py-14 md:py-20">
          <div className="w-full px-6 md:px-16 max-w-[1100px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-12">
              {/* Main */}
              <div className="flex-1 min-w-0">
                {/* Intro */}
                <p className="text-[#5a4035] text-[1.25rem] leading-[1.8] mb-10 font-medium border-l-4 border-[#c9a98a] pl-6">
                  {news.content.intro}
                </p>

                {/* Sections */}
                {news.content.sections.map((section, i) => (
                  <div key={i} className="mb-12">
                    <h2 className="text-[#3d2b1f] text-[1.5rem] font-bold leading-[1.4] mb-5">
                      <a id={`section-${i}`} className="text-[#3d2b1f]">{section.heading}</a>
                    </h2>
                    <p className="text-[#7a6455] text-[1.1875rem] leading-[1.85] mb-6">{section.body}</p>
                    {section.image && (
                      <div className="rounded-lg overflow-hidden w-full h-[280px] md:h-[360px] mb-4">
                        <img
                          src={section.image}
                          alt={section.heading}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    )}
                  </div>
                ))}

                {/* Closing */}
                <div className="p-6 bg-[#f5ede4] rounded-lg mb-10">
                  <p className="text-[#5a4035] text-[1.125rem] leading-[1.85] font-medium">{news.content.closing}</p>
                </div>

                {/* Prev / Next Navigation */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {prevNews ? (
                    <Link
                      to={`/news/${prevNews.id}`}
                      className="group flex items-center gap-4 p-5 bg-white rounded-lg border border-[#ede8e0] hover:border-[#c9a98a] transition-all cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center text-[#8B5E52] flex-shrink-0 group-hover:-translate-x-1 transition-transform">
                        <i className="ri-arrow-left-line text-xl"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[#a09080] text-[0.8125rem] mb-1">上一則</p>
                        <p className="text-[#3d2b1f] text-[0.9375rem] font-semibold leading-snug line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{prevNews.title}</p>
                      </div>
                    </Link>
                  ) : <div />}
                  {nextNews ? (
                    <Link
                      to={`/news/${nextNews.id}`}
                      className="group flex items-center gap-4 p-5 bg-white rounded-lg border border-[#ede8e0] hover:border-[#c9a98a] transition-all cursor-pointer sm:flex-row-reverse text-right"
                    >
                      <div className="w-8 h-8 flex items-center justify-center text-[#8B5E52] flex-shrink-0 group-hover:translate-x-1 transition-transform">
                        <i className="ri-arrow-right-line text-xl"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[#a09080] text-[0.8125rem] mb-1">下一則</p>
                        <p className="text-[#3d2b1f] text-[0.9375rem] font-semibold leading-snug line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{nextNews.title}</p>
                      </div>
                    </Link>
                  ) : <div />}
                </div>

                {/* CTA Row */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-[#ede8e0]">
                  <a
                    href="https://line.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2.5 px-6 py-4 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-bold hover:bg-[#5a3f30] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-line-fill text-[1.25rem]"></i> LINE 立即詢問
                  </a>
                  <Link
                    to="/appointment"
                    className="flex-1 flex items-center justify-center gap-2.5 px-6 py-4 border-2 border-[#3d2b1f] text-[#3d2b1f] rounded-full text-[1.0625rem] font-semibold hover:bg-[#f5ede4] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-2-line text-[1.25rem]"></i> 預約試穿
                  </Link>
                  <Link
                    to="/shop"
                    className="flex-1 flex items-center justify-center gap-2.5 px-6 py-4 bg-[#f5ede4] text-[#3d2b1f] rounded-full text-[1.0625rem] font-semibold hover:bg-[#e8d8c8] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-store-2-line text-[1.25rem]"></i> 瀏覽商品
                  </Link>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-[280px] flex-shrink-0">
                {/* Store Info */}
                <div className="p-6 bg-white rounded-lg mb-6">
                  <h4 className="text-[#3d2b1f] text-[1.125rem] font-bold mb-4 flex items-center gap-2">
                    <i className="ri-store-2-line text-[#8B5E52]"></i> 台中門市資訊
                  </h4>
                  <div className="space-y-3 text-[1rem]">
                    <div className="flex items-start gap-2.5">
                      <i className="ri-map-pin-line text-[#c9a98a] mt-0.5 flex-shrink-0"></i>
                      <span className="text-[#7a6455] leading-[1.6]">台中市西區精誠路 88 號</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <i className="ri-time-line text-[#c9a98a] mt-0.5 flex-shrink-0"></i>
                      <div className="text-[#7a6455] leading-[1.6]">
                        <p>週二至週日</p>
                        <p>10:00 – 19:00</p>
                        <p className="text-[#a09080] text-[0.9375rem]">週一公休</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <i className="ri-phone-line text-[#c9a98a] mt-0.5 flex-shrink-0"></i>
                      <a href="tel:+886423456789" className="text-[#7a6455] hover:text-[#3d2b1f] transition-colors cursor-pointer">04-2345-6789</a>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#3d2b1f] text-[#3d2b1f] rounded-full text-[0.9375rem] font-semibold hover:bg-[#f5ede4] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看地圖與預約
                  </Link>
                </div>

                {/* LINE CTA */}
                <div className="p-6 bg-[#f5ede4] rounded-lg mb-6">
                  <h4 className="text-[#3d2b1f] text-[1.125rem] font-bold mb-3">加入 LINE 不錯過</h4>
                  <p className="text-[#7a6455] text-[1rem] leading-[1.7] mb-4">
                    加入 LINE 官方帳號，第一手掌握新品到貨、限量活動與門市優惠資訊。
                  </p>
                  <a
                    href="https://line.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1rem] font-semibold hover:bg-[#5a3f30] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-line-fill"></i> 加入 LINE
                  </a>
                </div>

                {/* Other News */}
                <div>
                  <h4 className="text-[#3d2b1f] text-[1.125rem] font-bold mb-4">其他消息</h4>
                  <div className="space-y-4">
                    {otherNews.map(n => (
                      <Link
                        key={n.id}
                        to={`/news/${n.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-20 h-16 rounded-md overflow-hidden flex-shrink-0">
                          <img src={n.image} alt={n.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`inline-block px-2 py-0.5 rounded text-[0.75rem] font-semibold mb-1 ${tagColors[n.tag] || "bg-[#c9a98a] text-[#3d2b1f]"}`}>{n.tag}</span>
                          <h5 className="text-[#3d2b1f] text-[0.9375rem] font-semibold leading-[1.45] line-clamp-2 group-hover:text-[#8B5E52] transition-colors">{n.title}</h5>
                          <p className="text-[#a09080] text-[0.8125rem] mt-1">{n.date}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
